import securityCheck from "../../../../../core/services/securityService/adapter-license-check";
import adapterSecurityCheck from "../../../../../core/services/securityService/adapter-security-check";
import securityService from "../../../../../core/services/securityService/securityService";
import { DraftWizardsTypes } from "./Components/create-app-page-draft-adapter-modal";
import utils from "core/utils";
import { wizardIndexedDBService } from "../../../../../core/services/indexedDB/wizard-indexedDB-service";
import dateLib from "../../../../../core/utils/date-utils";
import api from "core/api/api";
import { isAppPresent } from "src/modules/wizards/app-wizards";
import componentService, {
    ComponentKeys,
    AdapterCategory,
    type componentID,
    type ModeInfo
} from "../../../../../core/services/componentService/component-service";
import metaStoreService from "../../../../../core/services/metaStoreService/meta-store-service";
import commonAdapterNameService from "../../../../../core/services/componentService/common-adapter-name-service";
import templateInfoService from "../../../../../core/services/componentService/topology-info-service";
import APPTYPES from "./services/create-app-page-app-types.const.json";

const getCustomCategory = (target, category) => {
    return ["DeltaLakeWriter", "FabricDataWarehouseWriter", "FabricMirrorWriter"].includes(target)
        ? "SQL2Fabric-X"
        : category;
};

export interface CreateAppPageAdapterData {
    id: string;
    value: string;
    type: "SOURCE" | "TARGET";
    category: string;
    icon: string;
    spriteId: string;
    newlyReleased: boolean;
    isPreviewAdapter: boolean;
    adapter: string;
    isEnabled: boolean;
    modes: string[];
    // For clubbed adapters: per-mode license state, resolved from each underlying IL/CDC adapter.
    ilModeInfo?: ModeInfo;
    cdcModeInfo?: ModeInfo;
}

export const createAppUtils = {
    formatHumanReadableName: function(name: string) {
        if (!name) return null;
        const keywords = ["INITIAL LOAD"];
        const matchedKeyword = keywords.find(keyword => name.toUpperCase().includes(keyword));

        if (matchedKeyword) {
            const caseInsensitiveKeyword = new RegExp(matchedKeyword, "i");
            name = name.replace(caseInsensitiveKeyword, "").trim();
        }
        return name as string;
    },
    isSourceAppAdapter: function(source: componentID) {
        return componentService.getCategory(source) === AdapterCategory.APPLICATIONS;
    },
    getCategoriesForFilters: function() {
        const sourceCategories = new Set();
        const targetCategories = new Set();
        const adaptersData = componentService.getCreateAppPageData();
        adaptersData.forEach(adapter => {
            if (!adapter.Category) {
                return;
            }
            if (adapter.Type === "TARGET") {
                targetCategories.add(adapter.Category);
            } else {
                sourceCategories.add(adapter.Category);
            }
        });
        const sourceCategoriesUnSorted = Array.from(sourceCategories)
            .map(category => ({
                label: category,
                value: category
            }))
            .filter(category => category.value !== "BASE");
        const targetCategoriesUnSorted = Array.from(targetCategories)
            .map(category => ({
                label: category,
                value: category
            }))
            .filter(category => category.value !== "BASE");

        const sourceCategoriesSorted = createAppUtils.sortCategories(sourceCategoriesUnSorted, "Source");
        const targetCategoriesSorted = createAppUtils.sortCategories(targetCategoriesUnSorted, "Target");

        return {
            sourceCategories: sourceCategoriesSorted,
            targetCategories: targetCategoriesSorted
        };
    },

    sortCategories: function(categories, type) {
        const sourceOrder = ["DATABASES", "APPLICATION", "CLOUD STORAGE", "DATA WAREHOUSE", "MISCELLANEOUS"];
        const targetOrder = [
            "DATA WAREHOUSE",
            "DATABASES",
            "APPLICATION",
            "EVENT BUS",
            "CLOUD STORAGE",
            "MISCELLANEOUS"
        ];
        const requiredOrder = type === "Source" ? sourceOrder : targetOrder;

        const sortedCategories = categories.sort((a, b) => {
            return requiredOrder.indexOf(a.value) - requiredOrder.indexOf(b.value);
        });

        return sortedCategories;
    },

    /**
     * TODO - Refactor this code to be more maintainable.
     */
    getSources: function(): CreateAppPageAdapterData[] {
        const sources = componentService.getCreateAppPageData(metaStoreService.entities.SOURCE as "SOURCE");

        const isFabricSolution = securityService.isFabricSolution();

        function checkIsAdapterEnabled(adapter, source) {
            if (isFabricSolution && adapter === "MSJet") {
                return false;
            }
            return securityCheck.checkWithLicenseValidation(adapter, source);
        }

        // For clubbed adapters, resolve each mode's own license state from its underlying adapter.
        function resolveModeInfo(modeInfo?: ModeInfo): ModeInfo | undefined {
            if (!modeInfo?.adapter) return modeInfo;
            return {
                ...modeInfo,
                isEnabled: adapterSecurityCheck.checkIfEnabled([modeInfo.adapter])
            };
        }

        let sourcesList = sources.map(source => {
            const sourceAdapter = source[ComponentKeys.ComponentName];
            const sourceComponent = source;
            const sourceName = sourceComponent[ComponentKeys.HumanReadableName];
            return {
                id: this.formatHumanReadableName(sourceName),
                componentName: sourceComponent[ComponentKeys.ComponentName],
                adapterName: sourceComponent[ComponentKeys.Adapter],
                value: sourceName,
                type: "SOURCE" as "SOURCE",
                icon: sourceComponent[ComponentKeys.ImageID],
                spriteId: sourceComponent[ComponentKeys.SpriteID],
                category: sourceComponent[ComponentKeys.Category],
                newlyReleased: sourceComponent[ComponentKeys.NewlyReleased] === "Yes",
                adapter: sourceAdapter,
                isEnabled: checkIsAdapterEnabled(sourceComponent[ComponentKeys.Adapter], source),
                modes: sourceComponent[ComponentKeys.Mode],
                ilModeInfo: resolveModeInfo(sourceComponent[ComponentKeys.ILModeInfo]),
                cdcModeInfo: resolveModeInfo(sourceComponent[ComponentKeys.CDCModeInfo])
            };
        });
        return sourcesList.sort((a, b) => {
            if (a.isEnabled !== b.isEnabled) {
                return a.isEnabled ? 1 : -1;
            }
            return a.id > b.id ? 1 : -1;
        });
    },
    getTargets: function(source: string): CreateAppPageAdapterData[] {
        if (!source) return [];

        let targets = componentService.getCreateAppPageData(metaStoreService.entities.TARGET as "TARGET");
        const isFabricSolution = securityService.isFabricSolution();

        const targetsList = targets.map(target => {
            const targetComponentName = target[ComponentKeys.ComponentName];
            const targetComponent = componentService.getMetadata(targetComponentName as componentID);
            let targetName = targetComponent[ComponentKeys.HumanReadableName];
            if (targetName.includes("Database") && targetName !== "Database") {
                targetName = targetName.replace("Database", "").trim();
            }

            const category = isFabricSolution
                ? getCustomCategory(target, targetComponent[ComponentKeys.Category])
                : targetComponent[ComponentKeys.Category];

            return {
                id: targetName,
                componentName: targetComponent[ComponentKeys.ComponentName],
                adapterName: targetComponent[ComponentKeys.Adapter],
                value: targetName,
                icon: targetComponent[ComponentKeys.ImageID],
                type: "TARGET",
                spriteId: targetComponent[ComponentKeys.SpriteID],
                category: category,
                newlyReleased: targetComponent[ComponentKeys.NewlyReleased] === "Yes",
                adapter: targetComponentName,
                isEnabled:
                    securityCheck.checkWithLicenseValidation(targetComponent[ComponentKeys.Adapter], targetName) ||
                    targetComponentName === "Striim",
                modes: targetComponent[ComponentKeys.Mode]
            };
        });
        return targetsList.sort((a, b) => {
            if (a.isEnabled !== b.isEnabled) {
                return a.isEnabled ? 1 : -1;
            }
            return a.id > b.id ? 1 : -1;
        });
    },
    /**
     * This function generates template url.
     * If the toppolgy supports schema conversion then url appends -to-create-schema.
     * If its an ILCDC Topology it appends -automated-ILCDC
     *
     * @param sourceAdapter
     * It should be the source component name
     *
     * @param targetAdapter
     * It should be the target component name
     *
     * @param appType
     * It should be either IL or CDC, It is used for adapters that are clubbed, to fetch its actual component name.
     * Eg : For Oracle, If appType is IL, the component name OracleIL is fetched.
     *
     * @returns `String` Template URL
     */
    generateTemplateURL: function(sourceAdapter, targetAdapter, appType) {
        if (!sourceAdapter || !targetAdapter) {
            return null;
        }
        sourceAdapter = commonAdapterNameService.getComponentName(sourceAdapter, appType) || sourceAdapter;

        let baseURL = `/appwizard/createapp/${sourceAdapter}-to-${targetAdapter}`;
        const isSchemaSupportedTopolgy = templateInfoService.isSchemaSupportedTopology(sourceAdapter, targetAdapter);

        if (isSchemaSupportedTopolgy) {
            baseURL = baseURL + "-to-create-schema";
        }

        if (appType === APPTYPES.AUTOMATEDILCDC) {
            baseURL = baseURL + "-automated-ILCDC";
        }

        return baseURL;
    },
    _formatStepName: function(stepName: string): string {
        if (!stepName) return null;
        const formattedStepName = utils.capitalizeAllWords(stepName.replaceAll("-", " "));
        return formattedStepName;
    },
    getAllDraftWizard: async function(wizardURL: string): Promise<DraftWizardsTypes[]> {
        try {
            let formattedDraftWizards = [];
            const templateID = wizardURL.split("createapp/")[1];
            const draftWizardsRaw = await wizardIndexedDBService.getAllDraftWizards();

            for (let i = 0; i < draftWizardsRaw?.length; i++) {
                const wizardData = draftWizardsRaw[i];

                // URL FORMAT -> "/appwizard/:app_name/:template_id/:component_id/:step_name"
                const splittedURL = wizardData?.step?.split("/");
                const draftAppName = splittedURL[2];
                const draftTemplatedID = splittedURL[3];
                const draftstepName = splittedURL[5];
                const [ns, appName] = draftAppName.split(".");

                const appPresent = await isAppPresent(draftAppName);
                if (appPresent) {
                    const appFQN = draftAppName.split(".").join(".APPLICATION.");
                    const result = await api.getApplicationStatus([appFQN]);
                    const formattedDate = dateLib(wizardData.date).format("MMM D, YYYY h:mm a");

                    if (result && result[appFQN] === "CREATED" && draftTemplatedID === templateID) {
                        formattedDraftWizards.push({
                            appName: appName,
                            namespace: ns,
                            url: wizardData.step.replace(/^#/, ""),
                            currentStep: this._formatStepName(draftstepName),
                            date: formattedDate
                        });
                    }
                } else {
                    wizardIndexedDBService.deleteDataById(draftAppName);
                }
            }
            return formattedDraftWizards;
        } catch (e) {
            console.error(e);
        }
    }
};
